'use client';

import { useTranslations } from 'next-intl';
import VacancyTile from '@/components/molecules/VacancyTile';
import { getVacancyDetailPath } from '@/utils/vacancy-url';
import type { PagePart_Vacancies_Entry } from '@/types/graphql';

interface VacancyItem {
    id?: string | null;
    title?: string | null;
    slug?: string | null;
    url?: string | null;
}

interface VacanciesProps {
    pagePart: PagePart_Vacancies_Entry;
    allVacancies?: VacancyItem[] | null;
    locale: string;
}

export default function Vacancies({ pagePart, allVacancies, locale }: VacanciesProps) {
    const t = useTranslations('Vacancies');
    const selected =
        pagePart.vacancies && pagePart.vacancies.length > 0
            ? (pagePart.vacancies as VacancyItem[])
            : (allVacancies ?? []);

    return (
        <div className="page-part-padding my-8 bg-haug-ivory-100 font-neue-haas lg:my-12">
            <div className="py-8 md:py-12">
                {(pagePart.title || pagePart.description) && (
                    <div className="mb-8">
                        {pagePart.title && (
                            <h2 className="font-bold text-2xl text-haug-choco-100 md:text-3xl lg:text-4xl">
                                {pagePart.title}
                            </h2>
                        )}
                        {pagePart.description && (
                            <div
                                className="mt-4 text-base text-haug-choco-100 [&_a]:underline"
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: CMS rich text content
                                dangerouslySetInnerHTML={{ __html: pagePart.description }}
                            />
                        )}
                    </div>
                )}

                {selected.length === 0 ? (
                <div className="rounded-lg border border-haug-choco-20 bg-haug-ivory-100 p-8 text-center">
                    <p className="font-neue-haas font-semibold text-haug-choco-100">
                        {t('emptyTitle')}
                    </p>
                    {t('emptyDescription') && (
                        <p className="mt-2 font-neue-haas text-haug-choco-70">
                            {t('emptyDescription')}
                        </p>
                    )}
                </div>
            ) : (
                <div className="flex w-full flex-col gap-4">
                    {selected.map((vacancy, index) => {
                        const slug = vacancy.slug;
                        if (!slug) return null;

                        const href = getVacancyDetailPath(locale, slug);

                        return (
                            <VacancyTile
                                key={vacancy.id ?? index}
                                href={href}
                                title={vacancy.title ?? ''}
                                viewLabel={t('viewVacancy')}
                            />
                        );
                    })}
                </div>
            )}
            </div>
        </div>
    );
}
